// FF-DOCK — Firefly chrome display mode.
// `floating` houses the bell's list inside Firefly.
// `docked` houses Firefly inside the notification bell.
// Stored `sidebar` (the retired rail chip) migrates to `docked`.

export const FIREFLY_DISPLAY_MODE_STORAGE_KEY = "firefly:display_mode";

export const FIREFLY_DISPLAY_MODES = ["floating", "docked"] as const;

export type FireflyDisplayMode = (typeof FIREFLY_DISPLAY_MODES)[number];

export const FIREFLY_DISPLAY_MODE_DEFAULT: FireflyDisplayMode = "floating";

export function parseFireflyDisplayMode(
  value: unknown,
  fallback: FireflyDisplayMode = FIREFLY_DISPLAY_MODE_DEFAULT,
): FireflyDisplayMode {
  if (typeof value !== "string") return fallback;
  if (value === "sidebar") return "docked";
  const match = FIREFLY_DISPLAY_MODES.find((mode) => mode === value);
  return match ?? fallback;
}

type Listener = (next: FireflyDisplayMode) => void;
const listeners = new Set<Listener>();

export function getFireflyDisplayMode(): FireflyDisplayMode {
  if (typeof localStorage === "undefined") {
    return FIREFLY_DISPLAY_MODE_DEFAULT;
  }
  try {
    return parseFireflyDisplayMode(localStorage.getItem(FIREFLY_DISPLAY_MODE_STORAGE_KEY));
  } catch {
    return FIREFLY_DISPLAY_MODE_DEFAULT;
  }
}

export function setFireflyDisplayMode(next: FireflyDisplayMode): void {
  if (typeof localStorage === "undefined") return;
  const value = parseFireflyDisplayMode(next);
  try {
    localStorage.setItem(FIREFLY_DISPLAY_MODE_STORAGE_KEY, value);
  } catch {
    /* quota / private mode */
  }
  for (const listener of listeners) listener(value);
}

export function subscribeFireflyDisplayMode(listener: Listener): () => void {
  listeners.add(listener);
  let storageHandler: ((e: StorageEvent) => void) | null = null;
  if (typeof window !== "undefined") {
    storageHandler = (e) => {
      if (e.key !== FIREFLY_DISPLAY_MODE_STORAGE_KEY) return;
      listener(parseFireflyDisplayMode(e.newValue));
    };
    window.addEventListener("storage", storageHandler);
  }
  return () => {
    listeners.delete(listener);
    if (storageHandler && typeof window !== "undefined") {
      window.removeEventListener("storage", storageHandler);
    }
  };
}
